import { Buffer } from 'buffer';
import { timelockEncrypt } from 'ts-ibe';

// Base URL for the chain API that exposes the IBE public key.
// Can be overridden via REACT_APP_CHAIN_API_URL; defaults to local node.
const CHAIN_API_BASE_URL =
  process.env.REACT_APP_CHAIN_API_URL || 'https://anon-testnet-api.fairblock.network';

// Cache IBE public keys so we only hit the chain API once per session.
let cachedIbePublicKeys = null;
let cachedIbePublicKeysPromise = null;

/**
 * Fetch active and queued IBE public keys from the chain API.
 * Expected endpoint: `${CHAIN_API_BASE_URL}/fairyring/keyshare/pubkey`
 * with response body containing:
 * { active_pubkey: { public_key: "..." }, queued_pubkey: { public_key: "..." }, ... }
 */
async function getIbePublicKeys() {
  if (cachedIbePublicKeys) {
    return cachedIbePublicKeys;
  }

  if (cachedIbePublicKeysPromise) {
    return cachedIbePublicKeysPromise;
  }

  const baseUrl = CHAIN_API_BASE_URL.replace(/\/$/, '');
  const url = `${baseUrl}/fairyring/keyshare/pubkey`;

  cachedIbePublicKeysPromise = (async () => {
    const response = await fetch(url, {
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      cachedIbePublicKeysPromise = null;
      throw new Error(
        `[IBE] Failed to fetch public keys from ${url}: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();

    const activePublicKey =
      data?.active_pubkey?.public_key || data?.active_pubkey?.pubkey;
    const queuedPublicKey =
      data?.queued_pubkey?.public_key || data?.queued_pubkey?.pubkey || null;

    if (!activePublicKey) {
      cachedIbePublicKeysPromise = null;
      throw new Error(
        '[IBE] Active public key not found in chain API response'
      );
    }

    cachedIbePublicKeys = {
      active: activePublicKey,
      queued: queuedPublicKey,
    };
    return cachedIbePublicKeys;
  })();

  return cachedIbePublicKeysPromise;
}

/**
 * Build the IBE identity from wallet address + chain master public key.
 * @param {string} userAddress
 * @param {string} publicKey - Active or queued master pubkey from chain
 * @returns {string}
 */
function buildIbeIdentity(userAddress, publicKey) {
  return `${userAddress.toLowerCase()}${publicKey}`;
}

/**
 * Encrypt a buffer with both active and queued IBE public keys.
 * Active ciphertext uses identity address+activePk; queued uses address+queuedPk.
 * @param {string} userAddress - User's wallet address
 * @param {Buffer} plaintextBuffer - Data to encrypt
 * @returns {Promise<{ active: string, queued: string | null, activeId: string, queuedId: string | null }>}
 */
async function encryptWithIbeKeys(userAddress, plaintextBuffer) {
  const { active, queued } = await getIbePublicKeys();

  const activeId = buildIbeIdentity(userAddress, active);
  const activeEncrypted = await timelockEncrypt(activeId, active, plaintextBuffer);

  let queuedEncrypted = null;
  let queuedId = null;
  if (queued) {
    queuedId = buildIbeIdentity(userAddress, queued);
    queuedEncrypted = await timelockEncrypt(queuedId, queued, plaintextBuffer);
  }

  return {
    active: activeEncrypted,
    queued: queuedEncrypted,
    activeId,
    queuedId,
  };
}

/**
 * Encrypts transfer amount using IBE (Identity-Based Encryption)
 * @param {string} userAddress - User's wallet address (combined with chain pk for IBE ID)
 * @param {string} chainKey - Chain identifier (deprecated, not used in ID)
 * @param {string|number} amount - Transfer amount to encrypt
 * @returns {Promise<{ active: string, queued: string | null, activeId: string, queuedId: string | null }>}
 */
export const encryptTransferAmount = async (userAddress, chainKey, amount) => {
  const amountString = typeof amount === 'number' ? amount.toString() : String(amount);
  const amountBuffer = Buffer.from(amountString, 'utf8');

  return encryptWithIbeKeys(userAddress, amountBuffer);
};

/**
 * Encrypts randomness (PedersenOpening) using IBE (Identity-Based Encryption)
 * @param {string} userAddress - User's wallet address (combined with chain pk for IBE ID)
 * @param {string} chainKey - Chain identifier (deprecated, not used in ID)
 * @param {string} randomnessBase64 - Base64-encoded randomness (PedersenOpening)
 * @returns {Promise<{ active: string, queued: string | null, activeId: string, queuedId: string | null }>}
 */
export const encryptRandomness = async (userAddress, chainKey, randomnessBase64) => {
  const randomnessBuffer = Buffer.from(randomnessBase64, 'base64');

  return encryptWithIbeKeys(userAddress, randomnessBuffer);
};
